import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  AttributeSchema,
  AttributeSchemaCreateRequest,
  AttributeSchemaListResponse,
  AttributeSchemaPatchRequest,
  Bom,
  BomActivateResponse,
  BomCreateRequest,
  BomExplosionResponse,
  BomListResponse,
  BomLinesReplaceRequest,
  BomOutputsReplaceRequest,
  BomPatchRequest,
  BomWithdrawResponse,
  CatalogueTemplate,
  ChangeLogListResponse,
  CostItemListResponse,
  CostRun,
  CostRunCreateRequest,
  CostRunListResponse,
  FloorCacheResponse,
  Product,
  ProductConversion,
  ProductConversionCreateRequest,
  ProductConversionListResponse,
  ProductCreateRequest,
  ProductDiscontinueRequest,
  ProductListResponse,
  ProductPatchRequest,
  QrPrintRequest,
  Routing,
  RoutingActivateResponse,
  RoutingCreateRequest,
  RoutingListResponse,
  RoutingPatchRequest,
  RoutingStepBindingListResponse,
  RoutingStepBindingsPutRequest,
  RoutingStepsReplaceRequest,
  RoutingWithdrawResponse,
  TemplateCreateRequest,
  TemplateListResponse,
  Uom,
  UomConversion,
  UomConversionCreateRequest,
  UomConversionListResponse,
  UomCreateRequest,
  UomListResponse,
  UomPatchRequest,
  VersionActivateRequest,
  VersionWithdrawRequest,
  WhereUsedResponse,
} from "./schemas.js";

// Module 01 — the full catalogue surface (spec 01 §5). Every mutation requires
// the Idempotency-Key header (TESTING §4.3); the generated client sends it per
// request. Mirrors identity/admin-register.ts. Cost-bearing responses are
// field-redacted server-side per §7 (the schemas mark cost fields `.optional()`).

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  idempotent?: boolean;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["catalogue"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation && config.idempotent !== false ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});

const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});

const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const pdf = (description: string) => ({
  200: {
    description,
    content: { "application/pdf": { schema: z.string().openapi({ format: "binary" }) } },
  },
});

const IdParam = z.object({ id: z.guid() });
const CursorQuery = z.object({
  cursor: z.guid().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
const UomDimensionQuery = z.enum([
  "length",
  "mass",
  "count",
  "area",
  "volume",
  "time",
  "container",
]);

// ===========================================================================
// products (spec 01 §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/products",
  operationId: "listProducts",
  summary: "List/search products (cursor paginated, tenant-scoped)",
  permission: "catalogue.read",
  description:
    "Filters: q (sku/name trigram), kind, status, schema_code, make_or_buy, and repeatable attr.<key> predicates (exact or gte:/lte: prefixes). Cost fields present only with catalogue.cost.read.",
  request: {
    query: z.object({
      cursor: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
      q: z.string().optional(),
      kind: z.string().optional(),
      status: z.string().optional(),
      schema_code: z.string().optional(),
      make_or_buy: z.string().optional(),
    }),
  },
  responses: ok("Products", ProductListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/products",
  operationId: "createProduct",
  summary: "Create a draft product (spec 01 §3.1)",
  permission: "catalogue.product.write",
  request: jsonBody(ProductCreateRequest),
  responses: {
    ...created("Draft product created", Product),
    409: problemResponse("SKU_TAKEN — sku already exists for this tenant"),
    422: problemResponse("UOM_NOT_FOUND / SCHEMA_VALIDATION_FAILED / TEMPLATE_NOT_FOUND"),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/products/{id}",
  operationId: "getProduct",
  summary: "Product detail (attribute schema, conversions, version summaries)",
  permission: "catalogue.read",
  request: { params: IdParam },
  responses: ok("Product", Product),
});
route({
  method: "patch",
  path: "/v1/catalogue/products/{id}",
  operationId: "updateProduct",
  summary: "Update product fields/attributes (sku immutable once active)",
  permission: "catalogue.product.write",
  request: { params: IdParam, ...jsonBody(ProductPatchRequest) },
  responses: {
    ...ok("Updated product", Product),
    422: problemResponse("SCHEMA_VALIDATION_FAILED — attributes fail the schema"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/activate",
  operationId: "activateProduct",
  summary: "Product draft → active (spec 01 §3.1)",
  permission: "catalogue.product.activate",
  request: { params: IdParam },
  responses: ok("Activated product", Product),
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/discontinue",
  operationId: "discontinueProduct",
  summary: "Product active → discontinued (spec 01 §3.1)",
  permission: "catalogue.product.discontinue",
  request: { params: IdParam, ...jsonBody(ProductDiscontinueRequest) },
  responses: {
    ...ok("Discontinued product", Product),
    409: problemResponse("PRODUCT_IN_ACTIVE_BOM — references[]; use force=true"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/reactivate",
  operationId: "reactivateProduct",
  summary: "Product discontinued → active (group_admin)",
  permission: "catalogue.product.reactivate",
  request: { params: IdParam },
  responses: ok("Reactivated product", Product),
});
route({
  method: "get",
  path: "/v1/catalogue/products/{id}/where-used",
  operationId: "getProductWhereUsed",
  summary: "BOM lines + outputs referencing this product",
  permission: "catalogue.structure.read",
  request: { params: IdParam },
  responses: ok("Where-used references", WhereUsedResponse),
});
route({
  method: "get",
  path: "/v1/catalogue/products/{id}/qr.pdf",
  operationId: "getProductQrPdf",
  summary: "Single product-card QR PDF (404 while draft)",
  permission: "catalogue.read",
  request: { params: IdParam },
  responses: pdf("Product-card PDF (QR + SKU + name + key attribute chips)"),
});
route({
  method: "post",
  path: "/v1/catalogue/products/qr-print",
  operationId: "printProductQrBatch",
  summary: "Batch QR card PDF (≤ 500 products, one card per page)",
  permission: "catalogue.read",
  request: jsonBody(QrPrintRequest),
  responses: {
    ...pdf("Batch PDF"),
    422: problemResponse("Draft products rejected with the offending ids"),
  },
});

// product-specific UoM conversions (spec 01 §2.5)
route({
  method: "get",
  path: "/v1/catalogue/products/{id}/conversions",
  operationId: "listProductConversions",
  summary: "Product-specific UoM conversion factors",
  permission: "catalogue.read",
  request: { params: IdParam },
  responses: ok("Conversions", ProductConversionListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/conversions",
  operationId: "createProductConversion",
  summary: "Add a product-specific cross-dimension conversion",
  permission: "catalogue.product.write",
  request: { params: IdParam, ...jsonBody(ProductConversionCreateRequest) },
  responses: created("Conversion created", ProductConversion),
});
route({
  method: "delete",
  path: "/v1/catalogue/conversions/{id}",
  operationId: "deleteProductConversion",
  summary: "Remove a product-specific conversion",
  permission: "catalogue.product.write",
  request: { params: IdParam },
  responses: { 204: { description: "Deleted" } },
});

// ===========================================================================
// units of measure + generic conversions (spec 01 §2.1, §2.2, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/uoms",
  operationId: "listUoms",
  summary: "List units of measure (base-uom picker feed)",
  permission: "catalogue.read",
  request: { query: z.object({ dimension: UomDimensionQuery.optional() }) },
  responses: ok("Units of measure", UomListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/uoms",
  operationId: "createUom",
  summary: "Create a unit of measure",
  permission: "catalogue.uom.manage",
  request: jsonBody(UomCreateRequest),
  responses: created("UoM created", Uom),
});
route({
  method: "patch",
  path: "/v1/catalogue/uoms/{id}",
  operationId: "updateUom",
  summary: "Edit a unit of measure",
  permission: "catalogue.uom.manage",
  request: { params: IdParam, ...jsonBody(UomPatchRequest) },
  responses: ok("Updated UoM", Uom),
});
route({
  method: "post",
  path: "/v1/catalogue/uoms/{id}/archive",
  operationId: "archiveUom",
  summary: "Archive a unit of measure (guarded by usage)",
  permission: "catalogue.uom.manage",
  request: { params: IdParam },
  responses: {
    ...ok("Archived UoM", Uom),
    409: problemResponse("UOM_IN_USE — reference counts"),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/uom-conversions",
  operationId: "listUomConversions",
  summary: "List generic same-dimension conversions",
  permission: "catalogue.read",
  responses: ok("Conversions", UomConversionListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/uom-conversions",
  operationId: "createUomConversion",
  summary: "Create a generic same-dimension conversion",
  permission: "catalogue.uom.manage",
  request: jsonBody(UomConversionCreateRequest),
  responses: created("Conversion created", UomConversion),
});
route({
  method: "delete",
  path: "/v1/catalogue/uom-conversions/{id}",
  operationId: "deleteUomConversion",
  summary: "Remove a generic conversion",
  permission: "catalogue.uom.manage",
  request: { params: IdParam },
  responses: { 204: { description: "Deleted" } },
});

// ===========================================================================
// attribute schemas (spec 01 §2.3, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/attribute-schemas",
  operationId: "listAttributeSchemas",
  summary: "Attribute schema registry",
  permission: "catalogue.read",
  responses: ok("Attribute schemas", AttributeSchemaListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/attribute-schemas",
  operationId: "createAttributeSchema",
  summary: "Create a custom attribute schema",
  permission: "catalogue.schema.manage",
  request: jsonBody(AttributeSchemaCreateRequest),
  responses: created("Schema created", AttributeSchema),
});
route({
  method: "patch",
  path: "/v1/catalogue/attribute-schemas/{id}",
  operationId: "updateAttributeSchema",
  summary: "Edit a schema (vertical-pack schemas via new version only)",
  permission: "catalogue.schema.manage",
  request: { params: IdParam, ...jsonBody(AttributeSchemaPatchRequest) },
  responses: ok("Updated schema", AttributeSchema),
});

// ===========================================================================
// BOMs (spec 01 §2.6–2.9, §3.3, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/products/{id}/boms",
  operationId: "listProductBoms",
  summary: "BOM version list for a product",
  permission: "catalogue.structure.read",
  request: {
    params: IdParam,
    query: z.object({ status: z.string().optional(), facility_id: z.guid().optional() }),
  },
  responses: ok("BOM versions", BomListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/boms",
  operationId: "createBomVersion",
  summary: "New draft BOM version (blank, from_template, or copy_from_version)",
  permission: "catalogue.bom.write",
  request: { params: IdParam, ...jsonBody(BomCreateRequest) },
  responses: created("Draft BOM created", Bom),
});
route({
  method: "get",
  path: "/v1/catalogue/boms/{id}",
  operationId: "getBom",
  summary: "Full BOM (lines, outputs, substitutes); ?explode=true for the tree",
  permission: "catalogue.structure.read",
  request: {
    params: IdParam,
    query: z.object({
      explode: z.coerce.boolean().optional(),
      levels: z.coerce.number().int().min(1).max(12).optional(),
      // Explosion scrap context (spec 01 §3.3.7, §5, M01-S05): with a facility
      // (and optional business date) the effective routing is resolved so each
      // line compounds component + operation + assembly scrap; without it, only
      // component + assembly scrap apply.
      facility_id: z.guid().optional(),
      date: z.string().optional(),
    }),
  },
  responses: ok(
    "BOM (full lines/outputs/substitutes), or the exploded tree when ?explode=true",
    z.union([Bom, BomExplosionResponse]),
  ),
});
route({
  method: "patch",
  path: "/v1/catalogue/boms/{id}",
  operationId: "updateBom",
  summary: "Edit draft BOM header",
  permission: "catalogue.bom.write",
  request: { params: IdParam, ...jsonBody(BomPatchRequest) },
  responses: {
    ...ok("Updated BOM", Bom),
    409: problemResponse(
      "VERSION_CONFLICT — header is not draft. The body names the version's status in `current_status`",
    ),
  },
});
route({
  method: "put",
  path: "/v1/catalogue/boms/{id}/lines",
  operationId: "replaceBomLines",
  summary: "Replace the full line set of a draft BOM",
  permission: "catalogue.bom.write",
  request: { params: IdParam, ...jsonBody(BomLinesReplaceRequest) },
  responses: {
    ...ok("Updated BOM", Bom),
    409: problemResponse("BOM_CYCLE_DETECTED / VERSION_CONFLICT"),
    422: problemResponse("UOM_NOT_CONVERTIBLE / per-line validation errors"),
  },
});
route({
  method: "put",
  path: "/v1/catalogue/boms/{id}/outputs",
  operationId: "replaceBomOutputs",
  summary: "Replace co-/by-product outputs of a draft BOM",
  permission: "catalogue.bom.write",
  request: { params: IdParam, ...jsonBody(BomOutputsReplaceRequest) },
  responses: {
    ...ok("Updated BOM", Bom),
    422: problemResponse("APPORTIONMENT_SUM_INVALID / co-/by-product rule errors"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/boms/{id}/activate",
  operationId: "activateBom",
  summary: "BOM draft → active (spec 01 §3.3)",
  permission: "catalogue.bom.activate",
  request: { params: IdParam, ...jsonBody(VersionActivateRequest) },
  responses: {
    ...ok("Activated BOM + superseded_version_id", BomActivateResponse),
    409: problemResponse("VERSION_CONFLICT — concurrent activation"),
    422: problemResponse("APPORTIONMENT_SUM_INVALID / UOM_NOT_CONVERTIBLE / EFFECTIVE_DATE_PAST"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/boms/{id}/archive",
  operationId: "archiveBom",
  summary: "Abandon (draft) or housekeep (superseded) a BOM version",
  permission: "catalogue.bom.activate",
  request: { params: IdParam },
  responses: {
    ...ok("Archived BOM", Bom),
    // Both refusals are decided under the same advisory lock as activate and
    // withdraw, and the archive UPDATE is conditional on the row's status at
    // write time (spec 01 §3.3.5 Archive, D-261 amended 2026-09-08).
    409: problemResponse(
      "BOM_NOT_ARCHIVABLE — status is not draft/superseded (checked at write time) / VERSION_STILL_RESUMABLE — a superseded version whose same-scope active successor is not yet in force is what withdrawing that successor would restore; withdraw first, then housekeep. A *_NOT_ARCHIVABLE body names the version's status in `current_status`, and the one decided at write time adds the status it judged as `judged_status`",
    ),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/boms/{id}/withdraw",
  operationId: "withdrawBom",
  summary: "Withdraw a not-yet-effective BOM activation (spec 01 §3.3.5, D-261)",
  // The inverse of `activateBom`, and the ONLY back-out a scheduled activation
  // has. It exists because three correct rules composed into a trap: §9.1
  // refuses an activation that is not strictly later, §9.2 refuses back-dating,
  // and `archiveBom` above accepts only draft/superseded — so a version
  // activated with a mistyped future date could never be undone, while §3.4
  // silently went on resolving TODAY to a stale older version.
  //
  // Permission is `catalogue.bom.activate`, the SAME code as activation, with
  // no new code minted: whoever may schedule a change may unschedule one that
  // has not yet taken effect, and a second code would leave every existing
  // planner able to create the mistake and unable to correct it.
  permission: "catalogue.bom.activate",
  request: { params: IdParam, ...jsonBody(VersionWithdrawRequest) },
  responses: {
    ...ok(
      "Withdrawn BOM + restored_version_id — null when the withdrawn version superseded nothing, which leaves the product with no active version (legal per §3.3.5; §3.4 then answers NO_EFFECTIVE_BOM)",
      BomWithdrawResponse,
    ),
    409: problemResponse(
      "VERSION_IN_FORCE — effective_from is on or before tenant-local today; a version that has taken effect is superseded by a newer one, never withdrawn / VERSION_NOT_ACTIVE — status is not active / PREDECESSOR_NOT_RESTORABLE — the predecessor recorded in catalogue_change_log is no longer superseded. A VERSION_NOT_ACTIVE body names the version's status in `current_status`",
    ),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/products/{id}/boms/effective",
  operationId: "resolveEffectiveBom",
  summary: "Resolve the effective BOM version (spec 01 §3.4)",
  permission: "catalogue.structure.read",
  request: {
    params: IdParam,
    query: z.object({ facility_id: z.guid().optional(), date: z.string().optional() }),
  },
  responses: {
    ...ok("Resolved BOM", Bom),
    404: problemResponse("NO_EFFECTIVE_BOM"),
  },
});

// ===========================================================================
// routings (spec 01 §2.10, §2.11, §3.5, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/products/{id}/routings",
  operationId: "listProductRoutings",
  summary: "Routing version list for a product",
  permission: "catalogue.structure.read",
  request: {
    params: IdParam,
    query: z.object({ status: z.string().optional(), facility_id: z.guid().optional() }),
  },
  responses: ok("Routing versions", RoutingListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/products/{id}/routings",
  operationId: "createRoutingVersion",
  summary: "New draft routing version (facility-bound)",
  permission: "catalogue.routing.write",
  request: { params: IdParam, ...jsonBody(RoutingCreateRequest) },
  responses: created("Draft routing created", Routing),
});
route({
  method: "get",
  path: "/v1/catalogue/routings/{id}",
  operationId: "getRouting",
  summary: "Full routing (steps)",
  permission: "catalogue.structure.read",
  request: { params: IdParam },
  responses: ok("Routing", Routing),
});
route({
  method: "patch",
  path: "/v1/catalogue/routings/{id}",
  operationId: "updateRouting",
  summary: "Edit draft routing header",
  permission: "catalogue.routing.write",
  request: { params: IdParam, ...jsonBody(RoutingPatchRequest) },
  responses: {
    ...ok("Updated routing", Routing),
    409: problemResponse(
      "VERSION_CONFLICT — routing is not draft. The body names the version's status in `current_status`",
    ),
  },
});
route({
  method: "put",
  path: "/v1/catalogue/routings/{id}/steps",
  operationId: "replaceRoutingSteps",
  summary: "Replace the step set of a draft routing (work-center facility match validated)",
  permission: "catalogue.routing.write",
  request: { params: IdParam, ...jsonBody(RoutingStepsReplaceRequest) },
  responses: {
    ...ok("Updated routing", Routing),
    422: problemResponse("Per-step validation errors (work center facility mismatch, batch_qty)"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/routings/{id}/activate",
  operationId: "activateRouting",
  summary: "Routing draft → active (spec 01 §3.5)",
  permission: "catalogue.routing.activate",
  request: { params: IdParam, ...jsonBody(VersionActivateRequest) },
  responses: {
    ...ok("Activated routing + superseded_version_id", RoutingActivateResponse),
    409: problemResponse("VERSION_CONFLICT — concurrent activation"),
    422: problemResponse("Work center facility mismatch / EFFECTIVE_DATE_PAST"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/routings/{id}/archive",
  operationId: "archiveRouting",
  summary: "Abandon (draft) or housekeep (superseded) a routing version",
  permission: "catalogue.routing.activate",
  request: { params: IdParam },
  responses: {
    ...ok("Archived routing", Routing),
    // Same rule and refusals as `archiveBom` (spec 01 §3.3.5 Archive, D-261
    // amended 2026-09-08).
    409: problemResponse(
      "ROUTING_NOT_ARCHIVABLE — status is not draft/superseded (checked at write time) / VERSION_STILL_RESUMABLE — a superseded version whose same-facility active successor is not yet in force is what withdrawing that successor would restore; withdraw first, then housekeep. A *_NOT_ARCHIVABLE body names the version's status in `current_status`, and the one decided at write time adds the status it judged as `judged_status`",
    ),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/routings/{id}/withdraw",
  operationId: "withdrawRouting",
  summary: "Withdraw a not-yet-effective routing activation (spec 01 §3.3.5, D-261)",
  // Same rule, same refusals and same response shape as `withdrawBom` above —
  // spec 01 §5 states the routing row as "as BOM" precisely so the two cannot
  // drift. Permission is `catalogue.routing.activate`, matching the routing
  // activation it undoes.
  permission: "catalogue.routing.activate",
  request: { params: IdParam, ...jsonBody(VersionWithdrawRequest) },
  responses: {
    ...ok(
      "Withdrawn routing + restored_version_id — null when the withdrawn version superseded nothing, which leaves the product with no active routing (legal per §3.3.5; §3.4 then answers NO_EFFECTIVE_ROUTING)",
      RoutingWithdrawResponse,
    ),
    409: problemResponse(
      "VERSION_IN_FORCE — effective_from is on or before tenant-local today / VERSION_NOT_ACTIVE — status is not active / PREDECESSOR_NOT_RESTORABLE — the predecessor recorded in catalogue_change_log is no longer superseded. A VERSION_NOT_ACTIVE body names the version's status in `current_status`",
    ),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/products/{id}/routings/effective",
  operationId: "resolveEffectiveRouting",
  summary: "Resolve the effective routing version (spec 01 §3.4; facility mandatory)",
  permission: "catalogue.structure.read",
  request: {
    params: IdParam,
    query: z.object({ facility_id: z.guid(), date: z.string().optional() }),
  },
  responses: {
    ...ok("Resolved routing", Routing),
    404: problemResponse("NO_EFFECTIVE_ROUTING"),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/routing-step-bindings",
  operationId: "listRoutingStepBindings",
  summary: "Read a facility's pack step-template → work-center bindings (spec 01 §2.18)",
  // A pure read of the binding set. It exists because spec 02 §4 S5's "Bind work
  // centers" panel has to show which pack step templates are still UNBOUND — the
  // `catalogue.routings_bound` go-live gate turns on exactly that, and a badge
  // derived from an unsaved client draft would be a guess, not ground truth.
  permission: "catalogue.structure.read",
  request: { query: z.object({ facility_id: z.guid() }) },
  responses: { ...ok("Binding set for the facility", RoutingStepBindingListResponse) },
});
route({
  method: "put",
  path: "/v1/catalogue/routing-step-bindings",
  operationId: "putRoutingStepBindings",
  summary: "Replace a facility's pack step-template → work-center bindings (spec 01 §2.18)",
  permission: "catalogue.template.manage",
  request: jsonBody(RoutingStepBindingsPutRequest),
  responses: {
    ...ok("Full binding set", RoutingStepBindingListResponse),
    422: problemResponse("Unknown refs or foreign-facility work centers"),
  },
});

// ===========================================================================
// floor cache + change log (spec 01 §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/floor-cache",
  operationId: "getFloorCache",
  summary: "Delta sync feeding the floor PWA offline cache (spec 01 §5, S9)",
  permission: "catalogue.read",
  request: {
    query: z.object({ facility_id: z.guid(), since: z.string().optional() }),
  },
  responses: ok(
    "Floor cache delta (products, routings, tombstones, next_since)",
    FloorCacheResponse,
  ),
});
route({
  method: "get",
  path: "/v1/catalogue/change-log",
  operationId: "listChangeLog",
  summary: "ECO history (spec 01 §2.12)",
  permission: "catalogue.read",
  request: {
    query: z.object({
      cursor: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
      product_id: z.guid().optional(),
      entity_type: z.enum(["bom", "routing"]).optional(),
      from: z.string().optional(),
      to: z.string().optional(),
    }),
  },
  responses: ok("Change-log entries", ChangeLogListResponse),
});

// ===========================================================================
// standard cost runs (spec 01 §2.15, §2.16, §3.7, §5) — cost.read gated
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/cost-runs",
  operationId: "listCostRuns",
  summary: "List standard cost runs",
  permission: "catalogue.cost.read",
  request: { query: CursorQuery },
  responses: ok("Cost runs", CostRunListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/cost-runs",
  operationId: "createCostRun",
  summary: "Create + compute a standard cost run (spec 01 §3.7)",
  permission: "catalogue.cost.run",
  request: jsonBody(CostRunCreateRequest),
  responses: created("Cost run created (computing)", CostRun),
});
route({
  method: "get",
  path: "/v1/catalogue/cost-runs/{id}",
  operationId: "getCostRun",
  summary: "Cost run detail",
  permission: "catalogue.cost.read",
  request: { params: IdParam },
  responses: {
    ...ok("Cost run", CostRun),
    404: problemResponse("COST_RUN_NOT_FOUND"),
  },
});
route({
  method: "get",
  path: "/v1/catalogue/cost-runs/{id}/items",
  operationId: "listCostRunItems",
  summary: "Per-product roll-up results (cursor paginated; filter min_delta_pct)",
  permission: "catalogue.cost.read",
  request: {
    params: IdParam,
    query: CursorQuery.extend({ min_delta_pct: z.coerce.number().optional() }),
  },
  responses: ok("Cost items", CostItemListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/cost-runs/{id}/release",
  operationId: "releaseCostRun",
  summary: "Release a computed cost run (snapshots standard costs onto products)",
  permission: "catalogue.cost.release",
  request: { params: IdParam },
  responses: {
    ...ok("Released cost run", CostRun),
    409: problemResponse("COST_RUN_NOT_RELEASABLE — not in a releasable status"),
  },
});
route({
  method: "post",
  path: "/v1/catalogue/cost-runs/{id}/discard",
  operationId: "discardCostRun",
  summary: "Discard a cost run",
  permission: "catalogue.cost.run",
  request: { params: IdParam },
  responses: ok("Discarded cost run", CostRun),
});

// ===========================================================================
// templates (spec 01 §2.17, §3.8, §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/catalogue/templates",
  operationId: "listTemplates",
  summary: "Template gallery",
  permission: "catalogue.read",
  request: { query: z.object({ kind: z.enum(["product", "bom", "routing"]).optional() }) },
  responses: ok("Templates", TemplateListResponse),
});
route({
  method: "post",
  path: "/v1/catalogue/templates",
  operationId: "createTemplate",
  summary: "Create a template (optionally from_entity snapshot)",
  permission: "catalogue.template.manage",
  request: jsonBody(TemplateCreateRequest),
  responses: created("Template created", CatalogueTemplate),
});
route({
  method: "post",
  path: "/v1/catalogue/templates/{id}/archive",
  operationId: "archiveTemplate",
  summary: "Archive a template",
  permission: "catalogue.template.manage",
  request: { params: IdParam },
  responses: ok("Archived template", CatalogueTemplate),
});
